import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { dashboardApi } from '@/services/api';
import { useThemeStore } from '@/store/themeStore';
import { AreaChart, Area, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { Users, AlertTriangle, CalendarDays, Inbox, ArrowUpRight, ArrowDownRight, ShieldAlert, Activity } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';

export default function MDDashboard() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', 'md'],
    queryFn: async () => {
      const res = await dashboardApi.md();
      return res.data.data;
    },
    refetchInterval: 300000, // 5 min refresh
  });

  if (isLoading || !data) {
    return (
      <div className="space-y-6">
        <div className="h-32 bg-slate-100 rounded-xl animate-pulse"></div>
        <div className="grid grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="h-32 bg-slate-100 rounded-xl animate-pulse"></div>)}
        </div>
      </div>
    );
  }

  // Calculate some derived stats
  const pendingApprovalsCount = data.summary?.pendingApprovals || 0;
  const isHighPending = pendingApprovalsCount > 5;
  const todayLeavesCount = data.summary?.onLeaveToday || 0;
  const totalEmployees = data.summary?.totalEmployees || 0;
  const upcomingLeavesCount = data.summary?.upcomingLeaves || 0;

  return (
    <div className="space-y-8 pb-8">
      <div className="flex justify-end">
        <Link to="/approvals/inbox">
          <Button variant="primary" className="shadow-sm">Go to Approval Inbox</Button>
        </Link>
      </div>

      {/* Top Stats Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Employees */}
        <div className="rounded-2xl p-6 bg-gradient-to-br from-blue-600 to-indigo-700 text-white shadow-lg relative overflow-hidden group">
          <div className="absolute right-0 top-0 w-32 h-32 bg-white/10 rounded-bl-full translate-x-8 -translate-y-8 transition-transform group-hover:scale-110"></div>
          <div className="relative z-10">
            <div className="flex justify-between items-start mb-4">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
                <Users className="w-5 h-5 text-white" />
              </div>
            </div>
            <h3 className="text-blue-100 text-sm font-medium mb-1">Total Employees</h3>
            <p className="text-4xl font-bold">{totalEmployees}</p>
          </div>
        </div>

        {/* On Leave Today */}
        <div className="rounded-2xl p-6 bg-gradient-to-br from-amber-500 to-orange-600 text-white shadow-lg relative overflow-hidden group">
          <div className="absolute right-0 top-0 w-32 h-32 bg-white/10 rounded-bl-full translate-x-8 -translate-y-8 transition-transform group-hover:scale-110"></div>
          <div className="relative z-10">
            <div className="flex justify-between items-start mb-4">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
                <CalendarDays className="w-5 h-5 text-white" />
              </div>
              <span className="text-xs font-bold bg-white/20 px-2 py-1 rounded-full flex items-center">
                {totalEmployees > 0 ? Math.round((todayLeavesCount / totalEmployees) * 100) : 0}% of workforce
              </span>
            </div>
            <h3 className="text-orange-100 text-sm font-medium mb-1">On Leave Today</h3>
            <p className="text-4xl font-bold">{todayLeavesCount}</p>
          </div>
        </div>

        {/* Pending Approvals */}
        <div className={`rounded-2xl p-6 text-white shadow-lg relative overflow-hidden group ${isHighPending ? 'bg-gradient-to-br from-red-500 to-rose-700' : 'bg-gradient-to-br from-emerald-500 to-teal-700'}`}>
          <div className="absolute right-0 top-0 w-32 h-32 bg-white/10 rounded-bl-full translate-x-8 -translate-y-8 transition-transform group-hover:scale-110"></div>
          <div className="relative z-10">
            <div className="flex justify-between items-start mb-4">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
                <Inbox className="w-5 h-5 text-white" />
              </div>
              {isHighPending && (
                <span className="flex items-center gap-1 text-xs font-bold bg-white/20 px-2 py-1 rounded-full animate-pulse-soft">
                  <AlertTriangle className="w-3 h-3" /> Action Required
                </span>
              )}
            </div>
            <h3 className="text-white/80 text-sm font-medium mb-1">Pending Approvals</h3>
            <p className="text-4xl font-bold">{pendingApprovalsCount}</p>
          </div>
        </div>

        {/* Upcoming Leaves (7 Days) */}
        <div className="rounded-2xl p-6 bg-gradient-to-br from-purple-600 to-fuchsia-700 text-white shadow-lg relative overflow-hidden group">
          <div className="absolute right-0 top-0 w-32 h-32 bg-white/10 rounded-bl-full translate-x-8 -translate-y-8 transition-transform group-hover:scale-110"></div>
          <div className="relative z-10">
            <div className="flex justify-between items-start mb-4">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
                <CalendarDays className="w-5 h-5 text-white" />
              </div>
            </div>
            <h3 className="text-purple-100 text-sm font-medium mb-1">Upcoming (Next 7 Days)</h3>
            <p className="text-4xl font-bold">{upcomingLeavesCount}</p>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Absenteeism Trend */}
        <div id="tour-md-summary" className="lg:col-span-2 card p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-bold text-slate-800">Absenteeism Trend</h2>
              <p className="text-sm text-slate-500">Last 12 months</p>
            </div>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.absenteeismTrend || []} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorDays" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: isDark ? '#94a3b8' : '#64748b' }} dy={10} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: isDark ? '#94a3b8' : '#64748b' }} />
                <Tooltip 
                  contentStyle={{ borderRadius: '12px', border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`, boxShadow: '0 10px 25px -5px rgba(0,0,0,0.2)', backgroundColor: isDark ? '#1e293b' : '#ffffff', color: isDark ? '#e2e8f0' : '#1e293b' }}
                />
                <Area type="monotone" dataKey="totalDays" stroke="#3b82f6" strokeWidth={3} fillOpacity={1} fill="url(#colorDays)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Leave Type Breakdown */}
        <div id="tour-md-heatmap" className="card p-6">
          <h2 className="text-lg font-bold text-slate-800 mb-1">Leave Distribution</h2>
          <p className="text-sm text-slate-500 mb-6">Current year</p>
          
          <div className="h-64 relative">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data.leaveTypeBreakdown || []}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="count"
                >
                  {data.leaveTypeBreakdown?.map((entry: any, index: number) => (
                    <Cell key={`cell-${index}`} fill={entry.color || ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444'][index % 5]} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ borderRadius: '12px', border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`, boxShadow: '0 10px 25px -5px rgba(0,0,0,0.2)', backgroundColor: isDark ? '#1e293b' : '#ffffff', color: isDark ? '#e2e8f0' : '#1e293b' }}
                  formatter={(value: any, name: any, props: any) => [value, props.payload.name]}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none flex-col">
              <span className="text-3xl font-bold text-slate-800">{data.leaveTypeBreakdown?.reduce((a:any,b:any)=>a+(b.count || 0),0) || 0}</span>
              <span className="text-xs text-slate-500">Total Leaves</span>
            </div>
          </div>
        </div>

      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* Bench Risk Alerts */}
        <div className="card p-6">
          <div className="flex items-center gap-2 mb-6">
            <ShieldAlert className="w-5 h-5 text-red-500" />
            <h2 className="text-lg font-bold text-slate-800">Bench Risk Alerts</h2>
          </div>
          
          {!(data.benchAlerts?.length) ? (
            <div className="text-center py-8">
              <ShieldAlert className="w-12 h-12 text-slate-200 mx-auto mb-3" />
              <p className="text-slate-500">No departments currently under staffing risk.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {data.benchAlerts?.map((risk: any, i: number) => (
                <div key={i} className="flex items-center justify-between p-4 bg-red-50 rounded-xl border border-red-100">
                  <div>
                    <h4 className="font-bold text-red-900">{risk.department}</h4>
                    <p className="text-sm text-red-700 mt-1">
                      {risk.onLeave} out of {risk.teamSize} employees on leave
                    </p>
                  </div>
                  <div className="text-right">
                    <span className="text-xl font-bold text-red-600">{risk.pct}%</span>
                    <p className="text-xs text-red-500 uppercase font-bold tracking-wider mt-1">Coverage Risk</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Top Leave Takers */}
        <div className="card p-6">
          <h2 className="text-lg font-bold text-slate-800 mb-6">Top Leave Takers</h2>
          <div className="space-y-5">
            {data.topLeaveTakers?.map((user: any, i: number) => (
              <Link key={user.id} to={`/admin/reports?tab=BALANCE&userId=${user.id}`} className="flex items-center gap-4 group">
                <span className="w-6 text-center font-bold text-slate-300 text-lg">#{i+1}</span>
                <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                  {user.photoUrl ? (
                    <img src={user.photoUrl} alt={user.name} className="w-full h-full rounded-full object-cover" />
                  ) : (
                    <span className="text-sm font-bold text-slate-500">{user.name.charAt(0)}</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-end mb-1">
                    <h4 className="font-semibold text-slate-800 truncate group-hover:text-blue-600">{user.name}</h4>
                    <span className="font-bold text-slate-700">{user.totalDays} days</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
                    <div 
                      className="h-1.5 rounded-full bg-blue-500" 
                      style={{ width: `${Math.min(100, (user.totalDays / 30) * 100)}%` }}
                    ></div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}
